import { useEffect } from 'react'
import { useRouter } from 'next/router'

import AnalyticsEvents_V2 from '~/analytics/analyticsGA4'
import { ConsentCategories } from '~/analytics/types/analytics.type'
import { trackingEventStore } from '~/behavior-tracking/utility/tracking-events-store'
import { getStoreUser } from '~/redux/store'
import appConfig from '~/config/app'

import { cleanNullAndUndefined2 } from './helpers'

export function initializeScripts(): void {
  if (typeof window === 'undefined') return // https://github.com/vercel/next.js/issues/5354#issuecomment-520305040
  // const isOfficialSite = process.env.NEXT_PUBLIC_IS_PRODUCTION_WEBSITE === 'true'

  window.dataLayer = window.dataLayer || new Proxy([], consentListenHandler)
  window.gtag = function gtag() {
    // eslint-disable-next-line fp/no-mutating-methods, prefer-rest-params
    window.dataLayer.push(arguments)
  }

  trackingEventStore.initState()
}

export function useTitle(title = ''): void {
  useEffect(() => {
    document.title = `${title}${title ? ' - ' : ''}House of Math`
    return () => {
      document.title = 'House of Math'
    }
  }, [title])
}

export function useNoScroll(disableScroll: boolean): void {
  const router = useRouter()

  useEffect(() => {
    const pathname = router.asPath
    const noScroll = disableScroll || (pathname.includes('games') && pathname.includes('multgame'))
    if (!noScroll) return
    const styles = ['overflow-y-hidden', 'max-h-screen']
    const root = document.getElementById('root') ?? document.body
    if (!root) return
    styles.forEach((style) => root.classList.add(style))
    return () => styles.forEach((style) => root.classList.remove(style))
  }, [router, disableScroll])
}

export function usePageTitle(title = ''): string {
  return `${title}${title ? ' | ' : ''}House of Math`
}

export function useNameBeforeTitle(title = ''): string {
  return `House of Math${title ? ' | ' : ''}${title}`
}

// cookie information does emit any event when the consent is updated,
// instead they push to dataLayer, this is a workaround to adjust our own analytics according to the consent
const consentListenHandler: ProxyHandler<Array<Record<string, any>>> = {
  set(target, property, value) {
    if (value?.event === ConsentCategories.Necessary && window.CookieInformation?.getConsentGivenFor !== undefined) {
      const cookieConsentStats = window.CookieInformation.getConsentGivenFor(ConsentCategories.Statistical)

      AnalyticsEvents_V2.setConsentStatus(cookieConsentStats)
    }
    // The Proxy itself stays in the school platform: the `Necessary` branch above is what resolves
    // consent, and without it AnalyticsEvents_V2 would stash every event forever and the first-party
    // /v1/process-events feed would silently stop. Only the marketing enrichment is switched off.
    if (value?.event === ConsentCategories.Marketing && !appConfig.isSchoolPlatform) {
      return trackingEventStore.getPseudoId().then((pseudoId) => {
        const user = getStoreUser()
        value = {
          ...value,
          ...cleanNullAndUndefined2({
            email: user?.facebookEmail ?? user?.authEmail ?? user?.email,
            phone: user?.phone,
            country_code: user?.countryCode?.toLowerCase(),
            pseudo_id: pseudoId,
          }),
        }
        return Reflect.set(target, property, value)
      })
    } else {
      return Reflect.set(target, property, value)
    }
  },
}
